import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';

const SEARCH_DEBOUNCE_MS = 450;

function MeiosSaidaListPage() {
  const { loading: authLoading, hasPermission } = useAuth();
  const canManage = hasPermission('config.meios_saida');

  const [meiosSaida, setMeiosSaida] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const perPageOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 'all', label: 'Todos' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (authLoading) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchMeiosSaida = async () => {
      setLoading(true);
      setError(null);

      if (!canManage) {
        setError({ kind: 'permission' });
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/meios-saida', {
          signal: controller.signal,
          params: {
            page: currentPage,
            per_page: perPage === 'all' ? 10000 : Number(perPage),
            status: statusFilter,
            search: debouncedSearch || undefined,
          },
        });
        setMeiosSaida(response?.data?.data ?? []);
        setPaginationInfo(response?.data?.meta ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchMeiosSaida();
    return () => controller.abort();
  }, [authLoading, canManage, currentPage, debouncedSearch, perPage, statusFilter]);

  const handlePageChange = (newPage) => {
    if (!paginationInfo) return;
    if (newPage < 1 || newPage > paginationInfo.last_page) return;
    setCurrentPage(newPage);
  };

  const totalRegistrosPaginacao = Number.isFinite(Number(paginationInfo?.total))
    ? Number(paginationInfo.total)
    : meiosSaida.length;

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza de que deseja desativar este meio de saída?')) {
      try {
        await api.delete(`/meios-saida/${id}`);
        setMeiosSaida((prev) => prev.filter((m) => m.id !== id));
      } catch {
        alert('Ocorreu um erro ao desativar o meio de saída.');
      }
    }
  };

  const handleRestore = async (id) => {
    if (window.confirm('Tem certeza de que deseja reativar este meio de saída?')) {
      try {
        await api.patch(`/meios-saida/${id}/restore`);
        setMeiosSaida((prev) => prev.filter((m) => m.id !== id));
      } catch {
        alert('Ocorreu um erro ao reativar o meio de saída.');
      }
    }
  };

  if (authLoading) {
    return (
      <div className="meios-saida-page">
        <LoadingSpinner text="Verificando sessão..." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="meios-saida-page">
        <LoadingSpinner text="Carregando meios de saída..." />
      </div>
    );
  }

  if (error) {
    if (error.kind === 'permission') {
      return (
        <div className="meios-saida-page">
          <PermissionDeniedAlert
            requiredPermission="config.meios_saida"
            action="visualizar a lista de meios de saída"
          />
        </div>
      );
    }
    return (
      <div className="meios-saida-page">
        <AlertException error={error} fallback="Não foi possível carregar os meios de saída." />
      </div>
    );
  }

  return (
    <div className="meios-saida-page">
      <div className="content-header">
        <h1 className="content-title">Meios de Saída</h1>
        <p className="content-subtitle">Lista de meios de saída cadastrados no sistema</p>
      </div>

      <div className="card meios-saida-list-card">
        <div className="card-body">
          <div className="list-toolbar meios-saida-list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="meios-saida-filters-group d-flex align-items-center gap-3">
              <div className="input-group meios-saida-search-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
              <div className="meios-saida-status-filter d-flex align-items-center gap-2">
                <label htmlFor="meiosSaidaStatusFilter" className="mb-0 meios-saida-status-label">Filtrar por:</label>
                <select
                  id="meiosSaidaStatusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Filtrar meios de saída por status"
                >
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary small">Registros por página</span>
                <AppSelect
                  className="meios-saida-per-page-select"
                  ariaLabel="Registros por página"
                  options={perPageOptions}
                  value={perPage}
                  onChange={handlePerPageChange}
                />
              </div>
            </div>

            <Link to="/painel/meios-saida/novo" className="btn btn-primary">
              <i className="bi bi-plus-lg me-2"></i>
              Novo Meio de Saída
            </Link>
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {meiosSaida.length > 0 ? (
                  meiosSaida.map((meio) => {
                    const isInactive = Boolean(meio.deleted_at);

                    return (
                      <tr key={meio.id}>
                        <td data-label="Nome">{meio.nome}</td>
                        <td data-label="Status">{isInactive ? 'Inativo' : 'Ativo'}</td>
                        <td className="table-actions meios-saida-table-actions d-flex gap-2" data-label="Ações">
                          {!isInactive && (
                            <Link
                              to={`/painel/meios-saida/${meio.id}/editar`}
                              className="btn btn-sm btn-outline-warning"
                              title="Editar"
                            >
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}
                          {!isInactive && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Desativar"
                              onClick={() => handleDelete(meio.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                          {isInactive && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              title="Reativar"
                              onClick={() => handleRestore(meio.id)}
                            >
                              <i className="bi bi-person-check"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center">Nenhum meio de saída encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && paginationInfo.last_page > 1 && perPage !== 'all' && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="meios-saida-pagination-summary">
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong> -{' '}
                <strong>{totalRegistrosPaginacao.toLocaleString('pt-BR')}</strong> registros
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page}
                >
                  Próximo <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MeiosSaidaListPage;
